// Student colors palette
export const STUDENT_COLORS = [
  { name: 'Rojo', value: '#EF4444' },
  { name: 'Naranja', value: '#F97316' },
  { name: 'Ámbar', value: '#F59E0B' },
  { name: 'Esmeralda', value: '#10B981' },
  { name: 'Cian', value: '#06B6D4' },
  { name: 'Azul', value: '#3B82F6' },
  { name: 'Violeta', value: '#8B5CF6' },
  { name: 'Rosa', value: '#EC4899' },
] as const;

// Task priority colors
export const PRIORITY_COLORS = {
  low: '#22C55E',      // green
  medium: '#F59E0B',   // amber
  high: '#F97316',     // orange
  urgent: '#EF4444',   // red
} as const;

// Task status colors
export const STATUS_COLORS = {
  pending: '#6B7280',      // gray
  in_progress: '#3B82F6',  // blue
  completed: '#22C55E',    // green
  cancelled: '#EF4444',    // red
} as const;

// Calendar event type colors
export const EVENT_TYPE_COLORS = {
  school_day: '#3B82F6',   // blue
  vacation: '#22C55E',     // green
  holiday: '#F59E0B',      // amber
  exam: '#EF4444',         // red
  meeting: '#8B5CF6',      // violet
  activity: '#06B6D4',     // cyan
  other: '#6B7280',        // gray
} as const;

// Grades (can be customized per country/school system)
export const GRADES = [
  'Preescolar',
  '1° Primaria',
  '2° Primaria',
  '3° Primaria',
  '4° Primaria',
  '5° Primaria',
  '6° Primaria',
  '1° Secundaria',
  '2° Secundaria',
  '3° Secundaria',
  '1° Preparatoria',
  '2° Preparatoria',
  '3° Preparatoria',
] as const;

// API endpoints
export const API_ENDPOINTS = {
  // Auth
  AUTH_REGISTER: '/api/auth/register',
  AUTH_LOGIN: '/api/auth/login',
  AUTH_LOGOUT: '/api/auth/logout',
  AUTH_ME: '/api/auth/me',
  AUTH_REFRESH: '/api/auth/refresh',

  // Students
  STUDENTS: '/api/students',
  STUDENT: (id: string) => `/api/students/${id}`,
  STUDENT_TASKS: (id: string) => `/api/students/${id}/tasks`,
  STUDENT_EVENTS: (id: string) => `/api/students/${id}/events`,

  // Tasks
  TASKS: '/api/tasks',
  TASK: (id: string) => `/api/tasks/${id}`,
  TASK_COMPLETE: (id: string) => `/api/tasks/${id}/complete`,
  TASK_STATUS: (id: string) => `/api/tasks/${id}/status`,
  TASKS_OVERDUE: '/api/tasks/overdue',
  TASKS_UPCOMING: '/api/tasks/upcoming',

  // Calendar
  CALENDAR_EVENTS: '/api/calendar/events',
  CALENDAR_EVENT: (id: string) => `/api/calendar/events/${id}`,
  CALENDAR_DAY: (date: string) => `/api/calendar/day/${date}`,
  CALENDAR_WEEK: (date: string) => `/api/calendar/week/${date}`,
  CALENDAR_MONTH: (date: string) => `/api/calendar/month/${date}`,

  // School years
  SCHOOL_YEARS: '/api/school-years',
  SCHOOL_YEAR: (id: string) => `/api/school-years/${id}`,
  SCHOOL_YEAR_ACTIVATE: (id: string) => `/api/school-years/${id}/activate`,

  // Vacations
  VACATIONS: '/api/vacations',
  VACATION: (id: string) => `/api/vacations/${id}`,

  // Notifications
  NOTIFICATIONS: '/api/notifications',
  NOTIFICATION_READ: (id: string) => `/api/notifications/${id}/read`,
  NOTIFICATIONS_READ_ALL: '/api/notifications/read-all',
  NOTIFICATIONS_UNREAD_COUNT: '/api/notifications/unread-count',

  // Uploads
  UPLOADS_PRESIGNED: '/api/uploads/presigned',
  UPLOADS_CONFIRM: '/api/uploads/confirm',
  UPLOAD: (id: string) => `/api/uploads/${id}`,

  // AI
  AI_EXTRACT_TASK: '/api/ai/extract-task',
  AI_SUGGEST_PRIORITY: '/api/ai/suggest-priority',
  AI_SUMMARIZE: '/api/ai/summarize',
} as const;

// Date formats
export const DATE_FORMATS = {
  display: 'dd/MM/yyyy',
  displayWithTime: 'dd/MM/yyyy HH:mm',
  api: 'yyyy-MM-dd',
  apiWithTime: "yyyy-MM-dd'T'HH:mm:ss",
  time: 'HH:mm',
  dayOfWeek: 'EEEE',
  monthYear: 'MMMM yyyy',
} as const;

// Animation durations (in seconds)
export const ANIMATION_DURATION = {
  instant: 0.1,
  fast: 0.15,
  normal: 0.2,
  slow: 0.3,
  slower: 0.5,
} as const;

// Animation easings
export const ANIMATION_EASE = {
  default: [0.25, 0.1, 0.25, 1],
  in: [0.4, 0, 1, 1],
  out: [0, 0, 0.2, 1],
  inOut: [0.4, 0, 0.2, 1],
} as const;

// ============================================
// AI PROVIDER MODELS
// ============================================
import type { AIProviderName, AIProviderConfig, PlanTier } from './types';

/**
 * Allowed models per provider. Used to populate the admin Select and to
 * validate the config on the backend. The FIRST entry of each provider is
 * treated as the sensible default when a stored model is missing/invalid.
 */
export const AI_MODELS: Record<AIProviderName, string[]> = {
  gemini: ['gemini-3.1-flash-lite', 'gemini-3-flash-preview'],
  anthropic: ['claude-haiku-4-5', 'claude-sonnet-4-6'],
  'workers-ai': ['@cf/google/gemma-4-26b-a4b-it'],
};

/** Human-friendly provider labels (Spanish UI) */
export const AI_PROVIDER_LABELS: Record<AIProviderName, string> = {
  gemini: 'Google Gemini',
  anthropic: 'Anthropic (Claude)',
  'workers-ai': 'Cloudflare Workers AI (Gemma)',
};

/** Default model for a given provider (first allowed model) */
export function defaultModelForProvider(provider: AIProviderName): string {
  return AI_MODELS[provider][0];
}

/**
 * Default provider configuration: ALL plans on Cloudflare Workers AI (Gemma 4
 * MoE), the primary model for the app. Used as the fallback when no config
 * exists in the database. Gemini/Anthropic remain selectable from the admin.
 */
export const DEFAULT_AI_PROVIDER_CONFIG: AIProviderConfig = {
  free: { provider: 'workers-ai', model: '@cf/google/gemma-4-26b-a4b-it' },
  premium: { provider: 'workers-ai', model: '@cf/google/gemma-4-26b-a4b-it' },
  family: { provider: 'workers-ai', model: '@cf/google/gemma-4-26b-a4b-it' },
};

/** Plan tiers in display order (used by the admin settings UI) */
export const AI_CONFIG_PLAN_TIERS: PlanTier[] = ['free', 'premium', 'family'];
